import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { getPrincipalInitials } from '../../../../models/NameUtils';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { baseStyleSheet } from '../../../styles/styles';
import { Avatar2 } from '../../Avatar2';
import { DangerIcon } from '../../svgs/icons/DangerIcon';
import { UserIconAvatar } from '../../UserIconAvatar';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	conversation: Api.ConversationViewModel;
	onClick(conversation: Api.ConversationViewModel): void;
	onMarkAsSpamClick(conversation: Api.ConversationViewModel): void;
	onMarkAsUnreadClick(conversation: Api.ConversationViewModel): void;
	onTrashClick(conversation: Api.ConversationViewModel): void;
	onUnsubscribeClick(conversation: Api.ConversationViewModel): void;
	selected?: boolean;
	selectedRecipient?: Api.TextRecipientViewModel;
}

export const ConversationItemBase: FC<IProps> = ({
	className = '',
	conversation,
	onClick,
	onMarkAsSpamClick,
	onMarkAsUnreadClick,
	onUnsubscribeClick,
	onTrashClick,
	selected,
	selectedRecipient,
}) => {
	const intervalRef = useRef<NodeJS.Timeout>(null);
	const [sinceLastMessage, setSinceLastMessage] = useState('');
	useEffect(() => {
		if (conversation?.lastMessage?.creationDate) {
			const creationDate = moment(conversation.lastMessage.creationDate);
			if (creationDate.isValid()) {
				setSinceLastMessage(Api.VmUtils.timeSince(creationDate));
			} else {
				setSinceLastMessage('---');
			}
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}

		if (conversation?.lastMessage?.creationDate) {
			intervalRef.current = setInterval(() => {
				const creationDate = moment(conversation.lastMessage.creationDate);
				if (creationDate.isValid()) {
					setSinceLastMessage(Api.VmUtils.timeSince(creationDate));
				} else {
					setSinceLastMessage('---');
				}
			}, 1000);
		}
	}, [conversation?.lastMessage]);

	const onConversationClick = () => {
		onClick(conversation);
	};
	const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Enter') {
			onClick(conversation);
		}
	};

	const onMarkConversationAsSpamClick = (e: MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		onMarkAsSpamClick(conversation);
	};

	const onUnsubscribeConversationClick = (e: MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		onUnsubscribeClick(conversation);
	};

	const onMarkConversationAsUnreadClick = (e: MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		onMarkAsUnreadClick(conversation);
	};

	const unsubscribedByConversation = !!conversation.automatedSMSOptOutDate;
	const unsubscribedByContact =
		!!selectedRecipient?.automatedSmsOptOutMetadata &&
		conversation.toNumbers?.length === 1 &&
		selectedRecipient?.id === conversation.toNumbers?.[0]?.contact?.id;
	const unsubscribed = unsubscribedByConversation || unsubscribedByContact;

	const avatarContact = conversation.toNumbers.length > 1 ? null : conversation.toNumbers[0].contact;

	return (
		<div
			className={`${css(styleSheet.container, selected && styleSheet.selected)} ${className}`}
			tabIndex={0}
			role='button'
			onKeyDown={onKeyDown}
			onClick={onConversationClick}
		>
			{conversation.unreadMessageCount > 0 && (
				<div className={css(baseStyleSheet.absoluteCenter, styleSheet.unreadDot)} />
			)}
			<div className={css(styleSheet.errorContainer)}>
				{!!conversation?.lastMessage?.errorMessage && <DangerIcon />}
			</div>
			<div className={css(styleSheet.avatarContainer)}>
				{avatarContact ? (
					<Avatar2
						fallbackText={getPrincipalInitials(avatarContact)}
						imgSrc={avatarContact?.profilePic}
						styles={[styleSheet.avatar]}
					/>
				) : (
					<UserIconAvatar styleDeclaration={styleSheet.avatar} />
				)}
			</div>
			<div className={css(styleSheet.contentWrapper)}>
				<div className={css(styleSheet.conversationNamesContainer)}>
					<p className={css(styleSheet.conversationNames, conversation.unreadMessageCount > 0 && styleSheet.unread)}>
						{Api.VmUtils.renderConversationNames(conversation)}
					</p>
					<Dropdown>
						<DropdownEllipsisTrigger styles={[styleSheet.moreOptionsTrigger]} />
						<DropdownContent styles={[styleSheet.menu]}>
							<DropdownItem onClick={onMarkConversationAsSpamClick}>Mark as spam</DropdownItem>
							{conversation.unreadMessageCount === 0 ? (
								<DropdownItem onClick={onMarkConversationAsUnreadClick}>Mark as unread</DropdownItem>
							) : null}
							{conversation.toNumbers?.length === 1 ? (
								<DropdownItem onClick={onUnsubscribeConversationClick}>
									{unsubscribedByConversation ? 'Subscribe' : 'Unsubscribe'}
								</DropdownItem>
							) : null}
							<DropdownItem onClick={() => onTrashClick(conversation)}>Delete</DropdownItem>
						</DropdownContent>
					</Dropdown>
				</div>
				<div className={css(styleSheet.description)}>
					{unsubscribed ? (
						<span className={css(styleSheet.unsubscribed)}>Unsubscribed</span>
					) : (
						!!conversation.lastMessage && (
							<span className={css(styleSheet.gray, styleSheet.lastMessage)}>
								{Api.VmUtils.getLastMessageText(conversation.lastMessage)}
							</span>
						)
					)}
					<span className={css(styleSheet.sinceLastMessage, baseStyleSheet.italicText)}>{sinceLastMessage}</span>
				</div>
			</div>
		</div>
	);
};

export const ConversationItem = observer(ConversationItemBase);
